import type { PostTypeFields } from '@tribeplatform/gql-client/graphql'
import type {
  PostType,
  MutationUpdatePostTypeFieldArgs,
  Network,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { getPostTypeKey, getPostTypesKey } from '../../utils/keys/post.key.js'

export const useUpdatePostTypeField = (options?: {
  fields?: PostTypeFields
  useMutationOptions?: UseMutationOptions<
    PostType,
    Error,
    MutationUpdatePostTypeFieldArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const postTypesKey = getPostTypesKey()
  const networkKey = getNetworkKey()

  return useMutation<PostType, Error, MutationUpdatePostTypeFieldArgs>(
    input => client.postType.updateField(input, fields),
    {
      onMutate: variables => {
        const postTypeKey = getPostTypeKey({ variables: { id: variables?.id } })

        queryClient.cancelQueries(postTypesKey)
        queryClient.cancelQueries(postTypeKey)

        queryClient.setQueriesData<Network>(networkKey, old => ({
          ...old,
          postTypes: old?.postTypes?.map(postType => ({
            ...postType,
            postFields: {
              fields: postType?.postFields?.fields?.map(field => ({
                ...field,
                ...(postType?.id === variables?.id &&
                  field?.key === variables?.input?.key &&
                  variables?.input),
              })),
            },
          })),
        }))
      },
      onSettled: variables => {
        const postTypeKey = getPostTypeKey({ variables: { id: variables?.id } })

        queryClient.invalidateQueries(postTypesKey)
        queryClient.invalidateQueries(postTypeKey)
        queryClient.invalidateQueries(networkKey)
      },
      ...useMutationOptions,
    },
  )
}
